/* =========================================================================
   Development Plan — read-time merge + layout + status helpers.

   `buildPlanNodes` is the FE selector that merges content (nodes/edges) with
   the player's state (progress) at read time — the "content × state merge at
   read" principle. Everything else (layout, edges, status) consumes the merged
   `PlanNode[]`. The radial/tech-tree layout is unchanged from the prototype.
   ========================================================================= */

import {
  DevPlanState,
  DevProfile,
  DevTrack,
  LastSoloResult,
  NodeStatus,
  PlanNode,
  PlanNodeLadder,
  Tier,
  TrackCode,
} from './types';

export const CORE_ID = 'core';

export const TIER_LABEL: Record<Tier, string> = {
  foundational: 'Foundational',
  core: 'Core',
  advanced: 'Advanced',
  mastery: 'Mastery',
};

export const TIER_ORDER: Record<Tier, number> = {
  foundational: 0,
  core: 1,
  advanced: 2,
  mastery: 3,
};

/* ---- Read-time content × state merge ------------------------------------ */

/**
 * Merge nodes + progress (rank) + edges (prereqs/soft) + node_games (count)
 * into the ergonomic `PlanNode[]` the UI renders. This is the single place the
 * flat wire arrays are joined by code — nothing else tree-walks.
 */
export function buildPlanNodes(state: DevPlanState): PlanNode[] {
  const rankByCode: Record<string, number> = {};
  const ownedByCode: Record<string, boolean> = {};
  state.progress.forEach((p) => {
    rankByCode[p.node_code] = p.rank;
    // Coin economy: a node is OWNED once purchased (unlocked_at stamped) —
    // rows with pre-coin rank progress are grandfathered in.
    ownedByCode[p.node_code] = Boolean(p.unlocked_at) || p.rank > 0;
  });

  const prereqs: Record<string, string[]> = {};
  const soft: Record<string, string[]> = {};
  state.edges.forEach((e) => {
    if (e.kind === 'prereq') {
      (prereqs[e.to] = prereqs[e.to] || []).push(e.from);
    } else if (e.kind === 'soft') {
      (soft[e.from] = soft[e.from] || []).push(e.to);
    }
  });

  const gameCount: Record<string, number> = {};
  state.node_games.forEach((g) => {
    gameCount[g.node_code] = (gameCount[g.node_code] || 0) + 1;
  });

  // Pre-rank warm-ups per node, tagged with completion (from ladders_done). Both arrays
  // are optional on the wire (legacy/mock) — guard with `?? []`.
  const ladderDone = new Set<string>();
  (state.ladders_done ?? []).forEach((d) => ladderDone.add(`${d.node_code}#${d.rank}`));
  const laddersByNode: Record<string, PlanNodeLadder[]> = {};
  (state.ladders ?? []).forEach((l) => {
    (laddersByNode[l.node_code] = laddersByNode[l.node_code] || []).push({
      rank: l.rank,
      asset_key: l.asset_key,
      done: ladderDone.has(`${l.node_code}#${l.rank}`),
    });
  });
  Object.values(laddersByNode).forEach((arr) => arr.sort((a, b) => a.rank - b.rank));

  // MOCK recommendations. Absent on the wire (the default for every trainee) => empty set =>
  // every node comes out `recommended: false` and nothing downstream changes. Matched on
  // `n.code`, exactly like the progress/edges joins above.
  const recommended = new Set(state.recommended ?? []);

  return state.nodes.map((n) => ({
    ...n,
    rank: rankByCode[n.code] ?? 0,
    owned: ownedByCode[n.code] ?? false,
    prereqs: prereqs[n.code] ?? [],
    soft: soft[n.code] ?? [],
    game_count: gameCount[n.code] ?? 0,
    ladders: laddersByNode[n.code] ?? [],
    recommended: recommended.has(n.code),
  }));
}

export function indexByCode(nodes: PlanNode[]): Record<string, PlanNode> {
  const byCode: Record<string, PlanNode> = {};
  nodes.forEach((n) => {
    byCode[n.code] = n;
  });
  return byCode;
}

/**
 * Set of game-asset keys that are unlocked for the player. A game (an asset
 * listed by one or more nodes via `node_games`) is unlocked when ANY node that
 * serves it has a PLAYABLE scenario — i.e. the node is already unlocked
 * (rank > 0) OR is available to unlock right now (`canUnlock`: prereqs + level
 * met). A game is locked only when every node serving it is itself locked and
 * unplayable. Games track scenario playability, not prior rank-up.
 *
 * This is the single shared source of truth for game gating: the Development
 * Plan node panel and the Games tab both consume it, so a game's locked/unlocked
 * state is identical in both places (see `nodeStatus` for the per-node rule).
 *
 * `enforceLocks` mirrors the progression-mode toggle: in "Open play" (false)
 * every game is unlocked, just as every node becomes playable — so games track
 * the same lifted locks the player sees on the nodes.
 */
export function unlockedGameKeys(
  state: DevPlanState,
  enforceLocks = true,
): Set<string> {
  if (!enforceLocks) {
    // Open play: unlock every game, matching the lifted node/scenario locks.
    return new Set(
      state.assets.filter((a) => a.kind === 'game').map((a) => a.key),
    );
  }
  const nodes = buildPlanNodes(state);
  const byCode = indexByCode(nodes);
  const playableNodes = new Set<string>();
  nodes.forEach((n) => {
    const st = nodeStatus(n, byCode, state);
    if (st.unlocked || st.canUnlock) playableNodes.add(n.code);
  });
  const keys = new Set<string>();
  state.node_games.forEach((g) => {
    if (playableNodes.has(g.node_code)) keys.add(g.asset_key);
  });
  return keys;
}

export interface XY {
  x: number;
  y: number;
}

/* ---- World geometry ----------------------------------------------------- */
export const WORLD_SIZE = 4200;
export const WORLD_CENTER = WORLD_SIZE / 2;

/* ---- Track orientation + grid spacing -----------------------------------
   Which arm of the cross a track occupies. This is a pure PRESENTATION choice —
   nothing about it comes from the wire (`DevTrack` carries code/name/blurb/color/
   order only), so changing an arm needs no seed, migration or re-seed.

   The map is resolved PER PLAN from the plan's own track set, because the arms
   are not independent: a quant-less tree frees the left arm, and re-hanging a
   track there would collide with quant on a tree that still has it. */
export interface TrackHeading {
  orient: 'v' | 'h';
  dir: number; // +1 = right/down, −1 = left/up
}

/** The 4-track shape (rokos-core, v1–v9): trader ↑, idea-generator →, risk ↓, quant ←. */
const CROSS_WITH_QUANT: Record<TrackCode, TrackHeading> = {
  trader: { orient: 'v', dir: -1 }, // up    (−y)
  'idea-generator': { orient: 'h', dir: 1 }, // right (+x)
  'risk-manager': { orient: 'v', dir: 1 }, // down  (+y)
  quant: { orient: 'h', dir: -1 }, // left  (−x)
  // Not used by a supported quant cross; retained so the heading table stays
  // total. layoutModeFor routes the Quant + Vol conflict away from this profile.
  'vol-academy': { orient: 'v', dir: 1 },
};

/** The 3-track shape (v10+, quant removed): trader ↑, idea-generator ←, risk →.
 *  Idea Generator takes the arm quant vacated and Risk Manager comes up off the
 *  bottom, so three tracks read as a T instead of leaving a hole on the left. */
const CROSS_WITHOUT_QUANT: Record<TrackCode, TrackHeading> = {
  ...CROSS_WITH_QUANT,
  'idea-generator': { orient: 'h', dir: -1 }, // left  (−x)
  'risk-manager': { orient: 'h', dir: 1 }, // right (+x)
};

/** The v13 shape: trader ↑, risk →, Vol Academy ↓, Idea Generator ←.
 *  v13 initially leaves the Idea Generator arm empty; adding that track later
 *  fills the left arm without moving the other three. Quant and Vol Academy do
 *  not share a supported cross profile and are routed to octi by layoutModeFor. */
const CROSS_WITH_VOL: Record<TrackCode, TrackHeading> = {
  trader: { orient: 'v', dir: -1 }, // up    (−y)
  'risk-manager': { orient: 'h', dir: 1 }, // right (+x)
  'vol-academy': { orient: 'v', dir: 1 }, // down  (+y)
  'idea-generator': { orient: 'h', dir: -1 }, // left  (−x)
  // Unused in this profile; see the conflict guard in layoutModeFor.
  quant: { orient: 'h', dir: -1 },
};

/** The arm assignment for one plan. Vol takes its dedicated v13 shape; otherwise
 *  the presence of the quant track selects the legacy four-arm shape.
 *  Takes anything with a `code` so the admin editor can pass its `trackCodes`. */
export function trackHeadings(
  tracks: ReadonlyArray<{ code: string }>
): Record<TrackCode, TrackHeading> {
  if (tracks.some((t) => t.code === 'vol-academy')) return CROSS_WITH_VOL;
  return tracks.some((t) => t.code === 'quant')
    ? CROSS_WITH_QUANT
    : CROSS_WITHOUT_QUANT;
}

/** Just the axis per track — what edge routing needs (`SkillEdges`). */
export function trackOrient(
  tracks: ReadonlyArray<{ code: string }>
): Record<TrackCode, 'v' | 'h'> {
  const headings = trackHeadings(tracks);
  return KNOWN_TRACK_CODES.reduce((acc, code) => {
    acc[code] = headings[code].orient;
    return acc;
  }, {} as Record<TrackCode, 'v' | 'h'>);
}

/** Legacy 4-arm axes. Only a fallback for callers with no plan in hand — prefer
 *  `trackOrient(tracks)`, which honours the quant-less shape. */
export const TRACK_ORIENT: Record<TrackCode, 'v' | 'h'> = {
  trader: CROSS_WITH_QUANT.trader.orient,
  'idea-generator': CROSS_WITH_QUANT['idea-generator'].orient,
  'risk-manager': CROSS_WITH_QUANT['risk-manager'].orient,
  quant: CROSS_WITH_QUANT.quant.orient,
  'vol-academy': CROSS_WITH_VOL['vol-academy'].orient,
};

const R0 = 360; // core → first layer along the primary axis
const LAYER_STEP_V = 165; // gap between layers on vertical tracks (Y)
const LAYER_STEP_H = 240; // gap between layers on horizontal tracks (X)
const SLOT_GAP_V = 200; // gap between sibling slots on vertical tracks (X)
const SLOT_GAP_H = 122; // gap between sibling slots on horizontal tracks (Y)

/**
 * Layout depth = longest *same-track* prerequisite chain. Cross-track prereqs
 * are ignored here (they become long link edges).
 */
export function computeDepths(nodes: PlanNode[]): Record<string, number> {
  const byCode = indexByCode(nodes);
  const memo: Record<string, number> = {};
  const depthOf = (code: string): number => {
    if (code in memo) return memo[code];
    const node = byCode[code];
    if (!node) return 0;
    memo[code] = 0; // guard against accidental cycles
    const sameTrack = node.prereqs.filter(
      (p) => byCode[p] && byCode[p].track_code === node.track_code
    );
    memo[code] = sameTrack.length ? 1 + Math.max(...sameTrack.map(depthOf)) : 0;
    return memo[code];
  };
  nodes.forEach((n) => depthOf(n.code));
  return memo;
}

/**
 * Centered (core = origin) grid positions. Per track: bucket nodes into layers
 * by depth, then lay each layer out in evenly-spaced slots centered on the axis.
 */
export function computeLayout(
  nodes: PlanNode[],
  tracks: DevTrack[]
): Record<string, XY> {
  const positions: Record<string, XY> = { [CORE_ID]: { x: 0, y: 0 } };
  const depths = computeDepths(nodes);
  const headings = trackHeadings(tracks);

  tracks.forEach((track) => {
    const { orient, dir } = headings[track.code];
    const layerStep = orient === 'v' ? LAYER_STEP_V : LAYER_STEP_H;
    const slotGap = orient === 'v' ? SLOT_GAP_V : SLOT_GAP_H;

    const layers: Record<number, PlanNode[]> = {};
    nodes
      .filter((n) => n.track_code === track.code)
      .forEach((n) => {
        const dep = depths[n.code];
        layers[dep] = layers[dep] || [];
        layers[dep].push(n);
      });

    Object.keys(layers).forEach((layerKey) => {
      const layer = Number(layerKey);
      const arr = layers[layer]
        .slice()
        .sort(
          (a, b) =>
            a.group.localeCompare(b.group) || a.code.localeCompare(b.code)
        );
      const m = arr.length;
      const primary = dir * (R0 + layer * layerStep);

      arr.forEach((node, k) => {
        const cross = (k - (m - 1) / 2) * slotGap;
        positions[node.code] =
          orient === 'v' ? { x: cross, y: primary } : { x: primary, y: cross };
      });
    });
  });

  return positions;
}

/* ---- Octilinear ("subway") fallback layout -----------------------------
   Plans whose tracks fit one of the cross profiles keep rendering
   with `computeLayout` above (untouched). Any other track shape — e.g. the
   11-track simple plan, whose codes aren't in TRACK_ORIENT — has no place in the
   4-quadrant cross, so it falls back to this octilinear layout: tracks radiate
   on 8 fixed headings and every node snaps to a distinct integer grid cell.
   With a cell pitch >= the node width, distinct cells make card overlaps
   impossible by construction. Same signature + centred (core at {0,0})
   convention as `computeLayout`, so the canvas consumes it identically. */

export const KNOWN_TRACK_CODES: TrackCode[] = [
  'trader',
  'idea-generator',
  'risk-manager',
  'quant',
  'vol-academy',
];

export type LayoutMode = 'cross' | 'octi';

/** Cross iff every track belongs to a supported profile and no more than four
 *  arms are needed. Quant and Vol Academy intentionally belong to different
 *  profiles, so a plan containing both falls back to the octilinear renderer. */
export function layoutModeFor(tracks: DevTrack[]): LayoutMode {
  const known = new Set<string>(KNOWN_TRACK_CODES);
  const codes = new Set(tracks.map((t) => t.code));
  const conflictingProfiles =
    codes.has('quant') && codes.has('vol-academy');
  return tracks.length > 0 &&
    tracks.length <= 4 &&
    tracks.every((t) => known.has(t.code)) &&
    !conflictingProfiles
    ? 'cross'
    : 'octi';
}

/** The world box the canvas renders into. Cross keeps the fixed legacy square;
 *  octi sizes to its (larger) bounds. `center` is where layout {0,0} (the core)
 *  sits in world space. */
export interface World {
  size: number;
  center: number;
}
export const DEFAULT_WORLD: World = { size: WORLD_SIZE, center: WORLD_CENTER };

/** A square world big enough to hold an octilinear layout, core kept centred. */
export function octiWorld(positions: Record<string, XY>): World {
  let half = 0;
  Object.values(positions).forEach((p) => {
    half = Math.max(half, Math.abs(p.x), Math.abs(p.y));
  });
  const size = 2 * (half + 320); // node half-extent + breathing room
  return { size, center: size / 2 };
}

// 8 grid headings, clockwise from up; row increases downward.
const OCTI_DIRS: Array<[number, number]> = [
  [0, -1], // N
  [1, -1], // NE
  [1, 0], // E
  [1, 1], // SE
  [0, 1], // S
  [-1, 1], // SW
  [-1, 0], // W
  [-1, -1], // NW
];

/**
 * Octilinear layout. Reuses the cross engine's depth metric (`computeDepths`),
 * then lays each track as a straight rail along one of 8 grid headings, siblings
 * offset perpendicular, and reused headings split into parallel rails. A
 * ring-search allocator only ever writes a node into a FREE cell, so every node
 * lands on a distinct cell — with PITCH >= node width, cards can't overlap.
 */
export function computeLayoutOcti(
  nodes: PlanNode[],
  tracks: DevTrack[]
): Record<string, XY> {
  // Locked-in values, tuned in the octi-v2 knob pane. Non-square cells (PITCH_X
  // vs PITCH_Y) pack rows tighter than columns, since the node card is wide but
  // short. Non-overlap holds as long as PITCH_X >= node width AND PITCH_Y >= node
  // height (the octi node CSS below stays within these). Fractional gaps are
  // rounded to the nearest cell by claim().
  const PITCH_X = 230;
  const PITCH_Y = 150;
  const CORE_GAP = 1.6;
  const LAYER_STEP = 1.8;
  const SIBLING_GAP = 2.5;
  const RAIL_GAP = 3;

  const depths = computeDepths(nodes);
  const occupied = new Set<string>();
  const key = (c: number, r: number) => `${c},${r}`;

  const claim = (idealC: number, idealR: number): XY => {
    const c0 = Math.round(idealC);
    const r0 = Math.round(idealR);
    if (!occupied.has(key(c0, r0))) {
      occupied.add(key(c0, r0));
      return { x: c0 * PITCH_X, y: r0 * PITCH_Y };
    }
    for (let radius = 1; radius < 256; radius += 1) {
      for (let dc = -radius; dc <= radius; dc += 1) {
        for (let dr = -radius; dr <= radius; dr += 1) {
          if (Math.max(Math.abs(dc), Math.abs(dr)) !== radius) continue;
          const c = c0 + dc;
          const r = r0 + dr;
          if (!occupied.has(key(c, r))) {
            occupied.add(key(c, r));
            return { x: c * PITCH_X, y: r * PITCH_Y };
          }
        }
      }
    }
    occupied.add(key(c0, r0)); // unreachable on any real plan
    return { x: c0 * PITCH_X, y: r0 * PITCH_Y };
  };

  const positions: Record<string, XY> = { [CORE_ID]: { x: 0, y: 0 } };
  occupied.add(key(0, 0));

  const dirUse: Record<number, number> = {};
  tracks.forEach((track, t) => {
    const di = Math.round((t * 8) / tracks.length) % 8;
    const use = dirUse[di] || 0;
    dirUse[di] = use + 1;
    // parallel offset for a reused heading: 0, +1, -1, +2, -2, ...
    const par =
      use === 0 ? 0 : use % 2 === 1 ? Math.ceil(use / 2) : -Math.ceil(use / 2);
    const [dc, dr] = OCTI_DIRS[di];
    const pc = -dr; // perpendicular (90° cw) — still one of the 8 headings
    const pr = dc;

    const layers: Record<number, PlanNode[]> = {};
    nodes
      .filter((n) => n.track_code === track.code)
      .forEach((n) => {
        const d = depths[n.code];
        (layers[d] = layers[d] || []).push(n);
      });

    Object.keys(layers)
      .map(Number)
      .sort((x, y) => x - y)
      .forEach((depth) => {
        const arr = layers[depth]
          .slice()
          .sort(
            (a, b) =>
              a.group.localeCompare(b.group) || a.code.localeCompare(b.code)
          );
        const m = arr.length;
        const along = CORE_GAP + depth * LAYER_STEP;
        arr.forEach((node, k) => {
          const perp = par * RAIL_GAP + (k - (m - 1) / 2) * SIBLING_GAP;
          positions[node.code] = claim(
            dc * along + pc * perp,
            dr * along + pr * perp
          );
        });
      });
  });

  return positions;
}

export interface Edge {
  id: string;
  source: string;
  target: string;
  /** true when the edge crosses track boundaries (drawn distinctly). */
  crossTrack: boolean;
  /** true for non-blocking 'soft' recommendation edges (drawn dashed/faint). */
  soft: boolean;
}

/**
 * Edges follow prerequisites (nodes with no prereq wire to the core so every
 * cluster grows out of the center) plus the non-blocking 'soft' recommendations.
 */
export function computeEdges(nodes: PlanNode[]): Edge[] {
  const edges: Edge[] = [];
  const byCode = indexByCode(nodes);

  nodes.forEach((node) => {
    if (node.prereqs.length === 0) {
      edges.push({
        id: `${CORE_ID}->${node.code}`,
        source: CORE_ID,
        target: node.code,
        crossTrack: false,
        soft: false,
      });
    } else {
      node.prereqs.forEach((prereqCode) => {
        const prereq = byCode[prereqCode];
        if (!prereq) return;
        edges.push({
          id: `${prereqCode}->${node.code}`,
          source: prereqCode,
          target: node.code,
          crossTrack: prereq.track_code !== node.track_code,
          soft: false,
        });
      });
    }

    node.soft.forEach((targetCode) => {
      const target = byCode[targetCode];
      if (!target) return;
      edges.push({
        id: `soft:${node.code}->${targetCode}`,
        source: node.code,
        target: targetCode,
        crossTrack: node.track_code !== target.track_code,
        soft: true,
      });
    });
  });

  return edges;
}

/** Collapse duplicate + collinear points so straight runs stay one segment. */
function simplifyPath(pts: XY[]): XY[] {
  const dedup: XY[] = [];
  pts.forEach((p) => {
    const last = dedup[dedup.length - 1];
    if (!last || Math.abs(p.x - last.x) > 0.5 || Math.abs(p.y - last.y) > 0.5) {
      dedup.push({ x: p.x, y: p.y });
    }
  });
  const out: XY[] = [];
  for (let i = 0; i < dedup.length; i += 1) {
    if (i === 0 || i === dedup.length - 1) {
      out.push(dedup[i]);
    } else {
      const a = out[out.length - 1];
      const b = dedup[i];
      const c = dedup[i + 1];
      const cross = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
      if (Math.abs(cross) > 0.5) out.push(b); // keep only real corners
    }
  }
  return out;
}

/** Orthogonal connector between two centered points, with rounded corners. */
export function orthoEdgePath(a: XY, b: XY, orient: 'v' | 'h'): string {
  const raw: XY[] =
    orient === 'v'
      ? [a, { x: a.x, y: (a.y + b.y) / 2 }, { x: b.x, y: (a.y + b.y) / 2 }, b]
      : [a, { x: (a.x + b.x) / 2, y: a.y }, { x: (a.x + b.x) / 2, y: b.y }, b];

  const pts = simplifyPath(raw).map((p) => ({
    x: WORLD_CENTER + p.x,
    y: WORLD_CENTER + p.y,
  }));
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;

  const R = 14; // corner radius
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 1; i < pts.length - 1; i += 1) {
    const prev = pts[i - 1];
    const p = pts[i];
    const next = pts[i + 1];
    const inLen = Math.hypot(p.x - prev.x, p.y - prev.y) || 1;
    const outLen = Math.hypot(next.x - p.x, next.y - p.y) || 1;
    const r = Math.min(R, inLen / 2, outLen / 2);
    const ix = p.x - ((p.x - prev.x) / inLen) * r;
    const iy = p.y - ((p.y - prev.y) / inLen) * r;
    const ox = p.x + ((next.x - p.x) / outLen) * r;
    const oy = p.y + ((next.y - p.y) / outLen) * r;
    d += ` L ${ix.toFixed(1)} ${iy.toFixed(1)} Q ${p.x.toFixed(1)} ${p.y.toFixed(
      1
    )} ${ox.toFixed(1)} ${oy.toFixed(1)}`;
  }
  const last = pts[pts.length - 1];
  d += ` L ${last.x.toFixed(1)} ${last.y.toFixed(1)}`;
  return d;
}

/**
 * Octilinear connector for the subway layout: a straight run plus one 45°
 * diagonal — every segment horizontal, vertical, or exactly 45°. `center`
 * offsets layout space into the (octi) world, mirroring how `orthoEdgePath`
 * offsets by WORLD_CENTER.
 */
export function octiEdgePath(a: XY, b: XY, center: number): string {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);
  const sx = Math.sign(dx);
  const sy = Math.sign(dy);
  const corner: XY =
    adx > ady
      ? { x: a.x + sx * (adx - ady), y: a.y }
      : { x: a.x, y: a.y + sy * (ady - adx) };

  const pts = [a, corner, b]
    .filter(
      (p, i, arr) =>
        i === 0 ||
        Math.abs(p.x - arr[i - 1].x) > 0.5 ||
        Math.abs(p.y - arr[i - 1].y) > 0.5
    )
    .map((p) => ({ x: center + p.x, y: center + p.y }));

  if (pts.length === 0) return '';
  if (pts.length === 1) return `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;

  const R = 14;
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 1; i < pts.length - 1; i += 1) {
    const prev = pts[i - 1];
    const p = pts[i];
    const next = pts[i + 1];
    const inLen = Math.hypot(p.x - prev.x, p.y - prev.y) || 1;
    const outLen = Math.hypot(next.x - p.x, next.y - p.y) || 1;
    const r = Math.min(R, inLen / 2, outLen / 2);
    const ix = p.x - ((p.x - prev.x) / inLen) * r;
    const iy = p.y - ((p.y - prev.y) / inLen) * r;
    const ox = p.x + ((next.x - p.x) / outLen) * r;
    const oy = p.y + ((next.y - p.y) / outLen) * r;
    d += ` L ${ix.toFixed(1)} ${iy.toFixed(1)} Q ${p.x.toFixed(1)} ${p.y.toFixed(
      1
    )} ${ox.toFixed(1)} ${oy.toFixed(1)}`;
  }
  const last = pts[pts.length - 1];
  d += ` L ${last.x.toFixed(1)} ${last.y.toFixed(1)}`;
  return d;
}

/**
 * Unlock status for a single node. v1 (mock) rule: unlocked when rank>0; ready
 * when every hard prereq is passed (rank>0, i.e. required_rank=1 — the only
 * value in the seed) and profile.level >= required_level. SP messaging is kept
 * for the mock's invest affordance; Phase 5 replaces invest with attempt-earn.
 */
/** Node purchase price from the payload's schedule (never hardcoded). */
export function unlockCostFor(node: PlanNode, state: DevPlanState): number {
  return state.economy?.unlock_cost?.[node.tier] ?? 60;
}

/** The coin price of one play at this node: an assessment scenario is priced
 *  by its own difficulty; an HTML rank-attempt by the node's tier. */
export function playCostFor(
  node: PlanNode,
  state: DevPlanState,
  difficulty?: string
): number {
  if (difficulty) return state.economy?.play_cost?.[difficulty] ?? 20;
  return state.economy?.tier_play_cost?.[node.tier] ?? 20;
}

/** Coin economy (2026-08-20): ownership is the ONE gate. Prereq edges are the
 *  recommended path — drawn, never enforced — and levels/skill points are
 *  retired. `canUnlock` means "affordable right now". */
export function nodeStatus(
  node: PlanNode,
  byCode: Record<string, PlanNode>,
  state: DevPlanState
): NodeStatus {
  const maxed = node.owned && node.rank >= node.max_rank;
  if (node.owned) {
    return {
      unlocked: true,
      canUnlock: false,
      maxed,
      reason: maxed
        ? 'Mastered — replay to beat your record'
        : node.rank > 0
        ? 'Owned — can rank up'
        : 'Owned — ready to play',
    };
  }
  const coins = state.wallet?.coins ?? 0;
  const cost = unlockCostFor(node, state);
  const canUnlock = coins >= cost;
  return {
    unlocked: false,
    canUnlock,
    maxed: false,
    reason: canUnlock
      ? `Unlock for ${cost} coins`
      : `Needs ${cost} coins — you have ${coins}`,
  };
}

/** Convert a hex color to an rgba() string (used for soft fills/glows). */
export function rgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => `${c}${c}`)
          .join('')
      : h;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** The scenario row's "last attempt" fragment: the score when there is one,
 *  otherwise what the room is waiting for — never "pending" when nothing is. */
export const lastResultMeta = (last?: LastSoloResult): string => {
  if (!last) return '';
  if (last.score_pct !== null && last.score_pct !== undefined)
    return ` · last ${Math.round(last.score_pct)}`;
  if (last.status === 'finished_ungraded') return ' · last being graded';
  if (last.status === 'deferred') return ' · last pending review';
  if (last.status === 'not_graded') return ' · last not graded';
  return '';
};
